import { Component, OnInit, OnDestroy, ViewChild, ViewContainerRef, ComponentFactoryResolver, ElementRef, Renderer2 } from '@angular/core';
import { SlidingPanelService } from '../../services/sliding-panel.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-sliding-panel',
  templateUrl: './sliding-panel.component.html',
  styleUrls: ['./sliding-panel.component.scss'],
})
export class SlidingPanelComponent implements OnInit, OnDestroy {
  isOpen = false;
  title = '';
  private subscriptions: Subscription[] = [];
  private isDragging = false;
  private startX: number = 0;
  private startWidth: number = 0;
  private readonly minWidth = 400; // Minimum width in pixels
  private readonly maxWidthPercent = 90; // Maximum width as percentage of viewport

  @ViewChild('contentContainer', { read: ViewContainerRef, static: true }) contentContainer!: ViewContainerRef;
  @ViewChild('panel', { static: true }) panel!: ElementRef;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private slidingPanelService: SlidingPanelService,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.slidingPanelService.isOpen$.subscribe(isOpen => {
        this.isOpen = isOpen;
      }),

      this.slidingPanelService.title$.subscribe(title => {
        this.title = title;
      }),

      this.slidingPanelService.component$.subscribe(componentData => {
        if (componentData) {
          this.loadComponent(componentData.component, componentData.data);
        } else {
          this.contentContainer.clear();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private loadComponent<T>(component: any, data?: any) {
    this.contentContainer.clear();
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);
    const componentRef = this.contentContainer.createComponent(componentFactory);
    
    if (data) {
      Object.assign(componentRef.instance, data);
    }

    this.slidingPanelService.setComponentRef(componentRef);
  }

  onClose() {
    this.slidingPanelService.close();
  }

  onDragStart(event: MouseEvent) {
    event.preventDefault();
    this.isDragging = true;
    this.startX = event.clientX;
    this.startWidth = this.panel.nativeElement.offsetWidth;

    // Add event listeners for dragging
    this.renderer.listen('document', 'mousemove', this.onDrag.bind(this));
    this.renderer.listen('document', 'mouseup', this.onDragEnd.bind(this));
    
    // Add dragging cursor to body
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  }

  private onDrag(event: MouseEvent) {
    if (!this.isDragging) return;

    const deltaX = this.startX - event.clientX;
    const newWidth = this.startWidth + deltaX;
    
    // Calculate max width based on viewport
    const maxWidth = (window.innerWidth * this.maxWidthPercent) / 100;
    
    // Apply width constraints
    const constrainedWidth = Math.max(this.minWidth, Math.min(maxWidth, newWidth));
    
    this.renderer.setStyle(this.panel.nativeElement, 'width', `${constrainedWidth}px`);
  }

  private onDragEnd() {
    this.isDragging = false;
    
    // Remove event listeners
    this.renderer.destroy();
    
    // Reset cursor
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }
} 